import type { SanitySlugData } from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import { cdnClient } from '@bulknutrients/bulk-shared-lib/lib/sanity/client';
import { randomiseOrder } from '@bulknutrients/bulk-shared-lib/utils';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Container } from '@components/Container';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import type { GetStaticProps } from 'next';
import Head from 'next/head';
import type React from 'react';
import type { ReactElement } from 'react';

export default function BuySiteMapPage({
  paths,
}: {
  paths: SanitySlugData[];
}): ReactElement {
  return (
    <>
      <Head>
        <title>Explore the Bulk Nutrients topic guides</title>
        <meta name="title" content="Explore the Bulk Nutrients topic guides" />
        <meta
          name="description"
          content="Explore our extensive topic guides. Easily navigate to find the information you need | Bulk Nutrients"
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/explore`}
        />
      </Head>
      <Container>
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            { title: 'Explore', path: '/explore' },
          ]}
        />
      </Container>
      <Container as="main" id="main-content">
        <h1>Bulk Nutrients' Topic Guides</h1>
        <div>
          <h2 className="capitalize">Explore</h2>
          <ul
            className="m-0 mb-5 ml-5 list-disc columns-(--max-column-count) p-0 pl-5"
            style={
              {
                '--max-column-count': 3,
              } as React.CSSProperties
            }
          >
            {paths.map((urlObj) => (
              <li key={urlObj.slug} className="my-1">
                <TextLink href={urlObj.slug}>
                  {urlObj.title || urlObj.slug}
                </TextLink>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </>
  );
}

BuySiteMapPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticProps: GetStaticProps = async () => {
  const data: SanitySlugData[] = await cdnClient.fetch(`//groq
    *[_type == "explorePage" && !(_id in path("drafts.**"))]{"slug": slug.current, title}
  `);

  // Return the URLs as props
  return {
    props: { paths: randomiseOrder(data) },
    revalidate: 86400, // Revalidate the data every day
  };
};
